import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import Card from "../Components/Card";
import BlueButton from "../Components/BlueButton";
import { BRAND } from "../css/brand";
import Inventory from "./Inventory";
import Entities from "./Entities";
import ReceivedKeysTable from "../Components/ReceivedKeysTable";
import AssignedKeysReport from "../Components/AssignedKeysReport";
import PullbackKeysReport from "../Components/PullbackKeysReport";
import UserProfileCard from "../Components/UserProfileCard";
import CustomerCards from "../Components/CustomerCard";
import RegisteredByDayChart from "../Components/RegisteredByDayChart";
import { fetchRoles, getRoleName } from "../Data/ROLES";
import { User, Key, FileText, Users, UserCircle } from "lucide-react";
import GoBackButton from "../Components/GoBackButton";
import UserViewSkeleton from "../Components/UserViewSkeleton";
import MonthwiseCustomersChart from "../Components/MonthwiseCustomersChart";


export default function UserViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  // console.log("role from user:" , user.role)
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("personal");
  const [activeTab1, setActiveTab1] = useState("receive");
  const [recommendedCount, setRecommendedCount] = useState(0);
  console.log("recommended count: ", recommendedCount)
  const [chalteCount, setChalteCount] = useState(0);

  const [kpis, setKpis] = useState([
    { label: "Active Retailers", value: "-" },
    // { label: "Pending Tickets", value: "-" },
    // { label: "New Signups", value: "-" },
    // { label: "Churn Risk", value: "-" },
  ]);

  const tabs = [
    { id: "personal", label: "Personal", icon: <User size={16} /> },
    { id: "keydetails", label: "Key Details", icon: <Key size={16} /> },
    { id: "keyreports", label: "Key Reports", icon: <FileText size={16} /> },
    ...(user?.role !== 7 ? [{ id: "retailers", label: "Users", icon: <Users size={16} /> }] : []),
    ...(user?.role === 7
      ? [{ id: "customers", label: "Customers", icon: <UserCircle size={16} /> }]
      : []),
  ];

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    async function fetchUser() {
      try {
        const res = await axios.get(`https://spillas.com/api/v1/org_user?id=${id}`);
        const userData = res.data?.data?.[0];
        setUser(userData || null);
      } catch (error) {
        console.error("Failed to load user details:", error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    }
    fetchUser();
  }, [id]);

  useEffect(() => {
    if (user?.id && user?.role) fetchDashboardCounts();
  }, [user]);

  useEffect(() => {
  if (user?.id && user?.role) {
    if (user.role === 7) {
      fetchRetailerCounts(); // 🔹 Special case for Retailer
    } else {
      fetchDashboardCounts(); // 🔹 Default logic for CNF / SS / Distributor
    }
  }
}, [user]);

const fetchDashboardCounts = async () => {
  try {
    setLoading(true);
    const response = await axios.post("https://spillas.com/api/v1/dashboard/count", {
      id: user.id,
      role: user.role,
    });

    const data = response.data.data || response.data;
    let dynamicLabel = "";
    let dynamicValue = 0;

    switch (Number(user.role)) {
      case 3:
        dynamicLabel = "Active Super Stockists";
        dynamicValue = data?.totals?.total_ss?.active || 0;
        break;
      case 4:
        dynamicLabel = "Active Distributors";
        dynamicValue = data?.totals?.total_dist?.active || 0;
        break;
      case 5:
      case 6:
        dynamicLabel = "Active Retailers";
        dynamicValue = data?.totals?.total_retailer?.active || 0;
        break;
      default:
        dynamicLabel = "Active Retailers";
        dynamicValue = data?.totals?.total_retailer?.active || 0;
    }

    setKpis((prev) =>
      prev.map((item, index) =>
        index === 0 ? { ...item, label: dynamicLabel, value: dynamicValue } : item
      )
    );
  } catch (err) {
    console.error("Error fetching dashboard data:", err);
  } finally {
    setLoading(false);
  }
};

const fetchRetailerCounts = async () => {
  try {
    setLoading(true);
    const toDate = new Date().toISOString().split("T")[0];
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 30);
    const formattedFromDate = fromDate.toISOString().split("T")[0];

    const url = `https://spillas.com/api/v1/get-customers-chart?from_date=${formattedFromDate}&to_date=${toDate}&company_id=${user.company_id}&retailer_id=${user.id}`;

    const res = await axios.get(url);
    const chartData = res.data?.data || res.data;

    // ✅ Safely extract counts from API structure
    const recommended = chartData?.summary?.total_recommended || 0;
    const chalte = chartData?.chalte_chalte?.totale_chalte_chalte || 0;

    setRecommendedCount(recommended);
    setChalteCount(chalte);

    // Optionally update KPI cards if needed
    setKpis((prev) =>
      prev.map((item, index) =>
        index === 0 ? { ...item, label: "Customers", value: recommended + chalte } : item
      )
    );
  } catch (error) {
    console.error("Error fetching retailer counts:", error);
    setRecommendedCount(0);
    setChalteCount(0);
  } finally {
    setLoading(false);
  }
};



  if (loading)
    return <UserViewSkeleton/>;

  if (!user)
    return (
      <div className="flex h-screen items-center justify-center text-red-500">
        Failed to load user details.
      </div>
    );

  return (
    <div className="min-h-screen px-2 sm:px-4 lg:px-8 py-4">
      <GoBackButton/>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        {/* Header Section */}
        <Card
          brand={BRAND}
          className="overflow-hidden border-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-xl rounded-3xl "
        >
  <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between w-full gap-6 px-4 sm:px-6">
  {/* LEFT — Profile Section */}
  <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4">
    <div className="grid h-16 w-16 place-items-center rounded-2xl bg-[#0E3F9E]/90 ring-1 ring-white/20 shadow-md">
      <ShieldCheck className="h-8 w-8 text-white" />
    </div>
    <div className="text-center sm:text-left">
      <div className="flex flex-wrap justify-center sm:justify-start items-center gap-2">
        <h1 className="text-lg sm:text-xl font-bold tracking-tight">
          {user.business_name || "—"}
        </h1>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
            user?.role === 3
              ? "bg-blue-500/20 text-blue-800"
              : user?.role === 4
              ? "bg-green-500/20 text-green-800"
              : user?.role === 5
              ? "bg-purple-500/20 text-purple-800"
              : user?.role === 7
              ? "bg-orange-500/20 text-orange-800"
              : "bg-gray-300 text-gray-700"
          }`}
        >
          {getRoleName(user?.role)}
        </span>
      </div>
      <div className="mt-2 flex flex-col sm:flex-row sm:flex-wrap items-center sm:items-start gap-2 text-xs text-blue-600">
        <a className="flex items-center gap-1" href={`mailto:${user.email}`}>
          <Mail className="h-3.5 w-3.5" /> {user.email || "—"}
        </a>
        <span className="flex items-center gap-1">
          <Phone className="h-3.5 w-3.5" /> {user.mobile || "—"}
        </span>
        <span className="flex items-center gap-1 text-center sm:text-left">
          <MapPin className="h-3.5 w-3.5" /> {user.district}, {user.state}{" "}
          {user.pincode || ""}
        </span>
      </div>
    </div>
  </div>

  {/* RIGHT — KPI + Summary */}
  <div className="flex flex-col lg:flex-row items-end justify-end gap-6 w-full lg:w-auto">
    {/* KPI Cards */}
    <div className="flex flex-wrap justify-end gap-4">
      {kpis.map((k, idx) => (
        <motion.div
          key={k.label}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 * idx }}
        >
          {/* <Card
            brand={BRAND}
            className="rounded-2xl border-slate-200/70 shadow-sm min-w-[160px] text-right"
          > */}
          <div className="rounded-2xl bg-white shadow-sm border border-slate-200/70 px-4 py-3 text-center min-w-[120px]">
          <p className="text-xs text-slate-500">{k.label}</p>
            <p className="mt-2 text-2xl font-bold text-slate-800 text-center">
              {loading && idx === 0 ? "Loading..." : k.value}
            </p>
            </div>
          {/* </Card> */}
        </motion.div>
      ))}
    </div>

    {/* Summary Cards */}
    <div className="flex items-center justify-end gap-3">
      <div className="rounded-2xl bg-white shadow-sm border border-slate-200/70 px-4 py-3 text-center min-w-[120px]">
        <p className="text-[11px] uppercase tracking-widest text-gray-500">
          Recommended
        </p>
        <p className="mt-1 text-2xl font-bold text-gray-900">
          {recommendedCount}
        </p>
      </div>
      <div className="rounded-2xl bg-white shadow-sm border border-gray-100 px-6 py-3 text-center min-w-[130px]">
        <p className="text-[11px] uppercase tracking-widest text-gray-500">
          Chalte
        </p>
        <p className="mt-1 text-2xl font-bold text-gray-900">{chalteCount}</p>
      </div>
    </div>
  </div>
</div>





          {/* KPI Cards */}
          {/* <div className="mt-6 grid gap-4 grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 px-4 sm:px-6">
            {kpis.map((k, idx) => (
              <motion.div
                key={k.label}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * idx }}
              >
                <Card brand={BRAND} className="rounded-2xl border-slate-200/70 shadow-sm">
                  <p className="text-xs text-slate-500">{k.label}</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-800">
                    {loading && idx === 0 ? "Loading..." : k.value}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div> */}


          {/* Tabs Section */}
          <div className="mt-8 w-full bg-gradient-to-r from-blue-700/40 to-indigo-700/40 rounded-xl px-2 sm:px-6 pb-6 pt-4">
            {/* Tab Buttons */}
            <div className="flex justify-center">
              <div className="flex flex-wrap justify-center bg-white/10 p-1 rounded-xl backdrop-blur-md space-x-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative flex items-center gap-1 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                      activeTab === tab.id
                        ? "text-blue-700"
                        : "text-white hover:text-blue-200"
                    }`}
                  >
                    {activeTab === tab.id && (
                      <motion.div
                        layoutId="active-pill"
                        className="absolute inset-0 bg-white rounded-lg shadow-md"
                        transition={{ type: "spring", duration: 0.5 }}
                      />
                    )}

                    {/* Icon (always visible, even on small screens) */}
                    <span className="relative z-10">{tab.icon}</span>

                    {/* Label (hidden on small screens) */}
                    <span className="relative z-10 hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>


            {/* Tab Content */}
            <div className="mt-6">
              {activeTab === "personal" && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <UserProfileCard user={user} onUpdate={setUser} />

                  {user?.role === 7 ? (
                    // 🔹 Retailer (Role 7)
                    <MonthwiseCustomersChart retailer_id={user.id} />
                  ) : [3, 4, 5].includes(user?.role) ? (
                    // 🔹 CNF / Superstockist / Distributor (Roles 3, 4, 5)
                    <RegisteredByDayChart user={user} />
                  ) : (
                    // 🔹 Fallback (optional)
                    <div className="flex items-center justify-center text-gray-500 text-sm border rounded-lg p-4">
                      No chart available for this role.
                    </div>
                  )}
                </div>
              )}

              {activeTab === "keydetails" && (
                <Inventory
                  receiverId={user.id}
                  companyId={user.company_id}
                  role={user.role}
                  compact
                />
              )}

              {activeTab === "keyreports" && (
                <div className="mt-4">
                  <div className="mb-6 flex flex-wrap gap-3 border-b border-slate-200 pb-2 justify-center sm:justify-start">
                    {[
                      { key: "receive", label: "Receive" },
                      { key: "assign", label: "Assign" },
                      { key: "pullback", label: "Pullback" },
                    ].map((tab) => (
                      <BlueButton
                        key={tab.key}
                        brand={BRAND}
                        variant="ghost"
                        onClick={() => setActiveTab1(tab.key)}
                        className={`rounded-xl px-4 py-2 text-sm font-medium transition-all ${
                          activeTab1 === tab.key
                            ? "bg-blue-600 text-white shadow"
                            : "text-slate-600 hover:bg-blue-50"
                        }`}
                      >
                        {tab.label}
                      </BlueButton>
                    ))}
                  </div>

                  <motion.div
                    key={activeTab1}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {activeTab1 === "receive" && (
                      <ReceivedKeysTable brand={BRAND} loginUserID={user.id} CompanyID={user.company_id} />
                    )}
                    {activeTab1 === "assign" && (
                      <AssignedKeysReport brand={BRAND} loginUserID={user.id} CompanyID={user.company_id} />
                    )}
                    {activeTab1 === "pullback" && (
                      <PullbackKeysReport brand={BRAND} receiverId={user.id} CompanyID={user.company_id} />
                    )}
                  </motion.div>
                </div>
              )}

              {activeTab === "retailers" && user.role !== 7 && (
                <Entities overrideUserId={user.id} overrideRole={user.role} compactView />
              )}

              {user.role === 7 && activeTab === "customers" && (
                <CustomerCards retailer_id={user.id} />
              )}
            </div>
          </div>
        </Card>
      </motion.div>
    </div>
  );
}


//   import React, { useEffect, useState } from "react";
//   import { useParams, useNavigate } from "react-router-dom";
//   import axios from "axios";
//   import { motion, AnimatePresence } from "framer-motion";
//   import { fetchRoles, getRoleName } from "../Data/ROLES";
//   import { BadgeCheck, Phone, Mail, MapPin, Shield, ShieldCheck } from "lucide-react";
//   import Card from "../Components/Card";
//   import BlueButton from "../Components/BlueButton";
//   import { BRAND } from "../css/brand";
// import Inventory from "./Inventory";
// import Entities from "./Entities";
// import ReceivedKeysTable from "../Components/ReceivedKeysTable";
// import AssignedKeysReport from "../Components/AssignedKeysReport";
// import PullbackKeysReport from "../Components/PullbackKeysReport";
// import UserProfileCard from "../Components/UserProfileCard";
// import CustomerCards from "../Components/CustomerCard";
// import RegisteredByDayChart from "../Components/RegisteredByDayChart";

//   export default function UserViewPage() {
//     const { id } = useParams();
//     const navigate = useNavigate();
//     const [user, setUser] = useState(null);
//     console.log("user data: " , user)
//     const [loading, setLoading] = useState(true);
//     const [activeTab, setActiveTab] = useState("personal");
//     const [activeTab1, setActiveTab1] = useState("receive");

//     const [kpis, setKpis] = useState([
//       { label: "Active Retailers", value: "-" },
//       { label: "Pending Tickets", value: "-" },
//       { label: "New Signups", value: "-" },
//       { label: "Churn Risk", value: "-" },
//     ]);

//     const tabs = [
//       { id: "personal", label: "Personal" },
//       { id: "keydetails", label: "Key Details" },
//       { id: "keyreports", label: "Key Reports" },
//       { id: "retailers", label: "Users" },
//       // 👇 Only show Customers tab for role = 7
//       ...(user?.role === 7 ? [{ id: "customers", label: "Customers" }] : []),
//     ];

//     useEffect(() => {
//         const loadRoles = async () => {
//           await fetchRoles(); // this populates ROLE_MAP globally
//         };
//         loadRoles();
//       }, []);

//     useEffect(() => {
//       async function fetchUser() {
//         try {
//           const res = await axios.get(`https://spillas.com/api/v1/org_user?id=${id}`);
//           console.log("user data:", res.data);
//           const userData = res.data?.data?.[0]; // ✅ Access first item
//           setUser(userData || null);
//         } catch (error) {
//           console.error("Failed to load user details:", error);
//           setUser(null);
//         } finally {
//           setLoading(false);
//         }
//       }
//       fetchUser();
//     }, [id]);

//     useEffect(() => {
//     if (!user?.id || !user?.role) return;
//     fetchDashboardCounts();
//   }, [user]);

//   const fetchDashboardCounts = async () => {
//     try {
//       setLoading(true);
//       const response = await axios.post("https://spillas.com/api/v1/dashboard/count", {
//         id: user.id,
//         role: user.role,
//       });

//       const data = response.data.data || response.data;

//       // Determine dynamic label & value based on user role
//       let dynamicLabel = "";
//       let dynamicValue = 0;

//       switch (Number(user.role)) {
//         case 3: // CNF
//           dynamicLabel = "Active Super Stockists";
//           dynamicValue = data?.totals?.total_ss?.active || 0;
//           break;
//         case 4: // Super Stockist
//           dynamicLabel = "Active Distributors";
//           dynamicValue = data?.totals?.total_dist?.active || 0;
//           break;
//         case 5: // Distributor
//           dynamicLabel = "Active Retailers";
//           dynamicValue = data?.totals?.total_retailer?.active || 0;
//           break;
//         case 6: // Retailer
//           dynamicLabel = "Active Retailers";
//           dynamicValue = data?.totals?.total_retailer?.active || 0;
//           break;
//         case 7: // Customer
//           dynamicLabel = "Active Customers";
//           dynamicValue = data?.customers?.total || 0;
//           break;
//         default:
//           dynamicLabel = "Active Retailers";
//           dynamicValue = data?.totals?.total_retailer?.active || 0;
//       }

//       // Update only the first KPI (Active Retailers → Dynamic Label & Value)
//       setKpis((prev) =>
//         prev.map((item, index) =>
//           index === 0
//             ? { ...item, label: dynamicLabel, value: dynamicValue }
//             : item
//         )
//       );
//     } catch (err) {
//       console.error("Error fetching dashboard data:", err);
//     } finally {
//       setLoading(false);
//     }
//   };
// useEffect(() => {
//     if (user?.id && user?.role) {
//       fetchDashboardCounts();
//     }
//   }, [user]);
//     if (loading)
//       return (
//         <div className="flex h-screen items-center justify-center text-gray-500">
//           Loading user details...
//         </div>
//       );

//     if (!user)
//       return (
//         <div className="flex h-screen items-center justify-center text-red-500">
//           Failed to load user details.
//         </div>
//       );

      

//     return (
//           <div className="min-h-screen ">
//       <div className="mx-auto p-4">
//         {/* Header card */}
//         <motion.div
//           initial={{ opacity: 0, y: 12 }}
//           animate={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.35 }}
//         >
//           <Card brand={BRAND} className="overflow-hidden border-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-xl">
//             {/* <CardContent className="p-0"> */}
//               <div className="flex flex-col gap-6 p-6 md:flex-row md:items-center md:justify-between">
//                 <div className="flex items-center gap-4">
//                   <div className="grid h-16 w-16 place-items-center rounded-2xl bg-[#0E3F9E]/90 text-white ring-1 ring-white/20 shadow-md">
//                     <ShieldCheck className="h-8 w-8" />
//                   </div>
//                   <div>
//                     <div className="flex flex-wrap items-center gap-2">
//                       <h1 className="text-xl font-bold tracking-tight">
//                         {user.business_name || "—"}
//                       </h1>
//                       <span
//                         className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
//                           user?.role === 3
//                             ? "bg-blue-500/20 text-blue-800"
//                             : user?.role === 4
//                             ? "bg-green-500/20 text-green-800"
//                             : user?.role === 5
//                             ? "bg-purple-500/20 text-purple-800"
//                             : user?.role === 7
//                             ? "bg-orange-500/20 text-orange-800"
//                             : "bg-gray-300 text-gray-700"
//                         }`}
//                       >
//                         {getRoleName(user?.role)}
//                       </span>

//                     </div>
//                     <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" style={{ color: "#0E3F9E" }}>
//                       <a
//                         className="flex items-center gap-1 hover:underline"
//                         href={`mailto:${user.email}`}
//                       >
//                         <Mail className="h-3.5 w-3.5" /> {user.email || "—"}
//                       </a>
//                       <span className="flex items-center gap-1">
//                         <Phone className="h-3.5 w-3.5" /> {user.mobile || "—"}
//                       </span>
//                       <span className="flex items-center gap-1">
//                         <MapPin className="h-3.5 w-3.5" />{" "}
//                         {user.district}, {user.state} {user.pincode || ""}
//                       </span>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Insight pills */}
//                 <div className="grid grid-cols-2 gap-3 sm:grid-cols-2">
//                   <div className="rounded-2xl bg-white/10 px-5 py-3 text-center shadow-sm ring-1 ring-white/15">
//                     <p className="text-[10px] uppercase tracking-widest opacity-80">
//                       Recommended
//                     </p>
//                     <p className="mt-1 text-2xl font-bold leading-none">
//                       {user.rec_count || 0}
//                     </p>
//                   </div>
//                   <div className="rounded-2xl bg-white/10 px-5 py-3 text-center shadow-sm ring-1 ring-white/15">
//                     <p className="text-[10px] uppercase tracking-widest opacity-80">
//                       Chalte
//                     </p>
//                     <p className="mt-1 text-2xl font-bold leading-none">
//                       {user.chalte_count || 0}
//                     </p>
//                   </div>
//                 </div>
//               </div>

//               <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
//                 {kpis.map((k, idx) => (
//                   <motion.div
//                     key={k.label}
//                     initial={{ opacity: 0, y: 8 }}
//                     animate={{ opacity: 1, y: 0 }}
//                     transition={{ delay: 0.05 * idx }}
//                   >
//                     <Card brand={BRAND} className="rounded-2xl border-slate-200/70 shadow-sm">
//                       <p className="text-xs text-slate-500">{k.label}</p>
//                       <p className="mt-2 text-2xl font-semibold text-slate-800">
//                         {loading && idx === 0 ? "Loading..." : k.value}
//                       </p>
//                     </Card>
//                   </motion.div>
//                 ))}
//               </div>

//                <div className="w-full bg-gradient-to-r from-blue-700/40 to-indigo-700/40 px-6 pb-4 pt-3 rounded-xl">
//                 <div className="flex items-center justify-center">
//                   <div className="flex space-x-2 bg-white/10 p-1 rounded-xl backdrop-blur-md">
//                     {tabs.map((tab) => (
//                       <button
//                         key={tab.id}
//                         onClick={() => setActiveTab(tab.id)}
//                         className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
//                           activeTab === tab.id
//                             ? "text-blue-700"
//                             : "text-white hover:text-blue-200"
//                         }`}
//                       >
//                         {activeTab === tab.id && (
//                           <motion.div
//                             layoutId="active-pill"
//                             className="absolute inset-0 bg-white rounded-lg shadow-md"
//                             transition={{ type: "spring", duration: 0.5 }}
//                           />
//                         )}
//                         <span className="relative z-10">{tab.label}</span>
//                       </button>
//                     ))}


//                   </div>
//                 </div>

//                 {/* ✅ Tab Content */}
//                 <div className="mt-6">
//                    {activeTab === "personal" && (
//                     <div className="mt-6 grid gap-6 lg:grid-cols-2">
//                       {/* Profile card */}
//                       <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
//                           <UserProfileCard user={user} onUpdate={setUser} />
//                       </motion.div>

//                       {/* Chart Card (Updated Dropdowns) */}
//                       <motion.div
//                         initial={{ opacity: 0, y: 12 }}
//                         animate={{ opacity: 1, y: 0 }}
//                         transition={{ delay: 0.05 }}
//                         className=""
//                       >
//                         <RegisteredByDayChart retailerId={user.id} loginId={user.id}/>
//                       </motion.div>
//                     </div>
//                     )}
//                     {activeTab === "keydetails" && (
//                       <div className="mt-4">
//                         <Inventory
//                           receiverId={user.id}           // from user details
//                           companyId={user.company_id}    // from user data
//                           role={user.role}               // e.g., 3, 4, 5, or 7
//                           compact={true}                 // hides header/pagination
//                         />
//                       </div>
//                     )}
//                   {activeTab === "keyreports" && (
//                     <div className="p-4">
//                       {/* 🟦 Tabs Header */}
//                       <div className="mb-6 flex gap-3 border-b border-slate-200 pb-2">
//                         {[
//                           { key: "receive", label: "Receive" },
//                           { key: "assign", label: "Assign" },
//                           { key: "pullback", label: "Pullback" },
//                         ].map((tab) => (
//                           <BlueButton brand={BRAND}
//                             key={tab.key}
//                             variant="ghost"
//                             onClick={() => setActiveTab1(tab.key)}
//                             className={`rounded-xl px-4 py-2 text-sm font-medium transition-all duration-200 ${
//                               activeTab === tab.key
//                                 ? "bg-blue-600 text-white shadow"
//                                 : "text-slate-600 hover:bg-blue-50"
//                             }`}
//                           >
//                             {tab.label}
//                           </BlueButton>
//                         ))}
//                       </div>

//                       {/* 🧩 Tab Content */}
//                       <motion.div
//                         key={activeTab1}
//                         initial={{ opacity: 0, y: 10 }}
//                         animate={{ opacity: 1, y: 0 }}
//                         transition={{ duration: 0.3 }}
//                       >
//                         {activeTab1 === "receive" && (
//                           <ReceivedKeysTable
//                             brand={BRAND}
//                             loginUserID={user.id}
//                             CompanyID={user.company_id}
//                           />
//                         )}

//                         {activeTab1 === "assign" && (
//                           <AssignedKeysReport
//                             brand={BRAND}
//                             loginUserID={user.id}
//                             CompanyID={user.company_id}
//                           />
//                         )}

//                         {activeTab1 === "pullback" && (
//                           <PullbackKeysReport
//                             brand={BRAND}
//                             receiverId={user.id}
//                             CompanyID={user.company_id}
//                           />
//                         )}
//                       </motion.div>
//                     </div>
//                   )}
//                   {activeTab === "retailers" && user.role !== 7 && (
//                     <Entities
//                       overrideUserId={user.id}
//                       overrideRoleId={user.role}   // Pass numeric role ID
//                       compactView
//                     />
//                   )}
//                   {user.role === 7 && activeTab === "customers" && <CustomerCards company_id={user.company_id} login_id={user.id} />}
//                 </div>
//               </div>
//             {/* </CardContent> */}
//           </Card>
//         </motion.div>
       
//       </div>
//       </div>
//     );
//   }


// import React, { useEffect, useState } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import axios from "axios";
// import { motion } from "framer-motion";
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   ResponsiveContainer,
// } from "recharts";
// import { BadgeCheck, Phone, Mail, MapPin, Shield } from "lucide-react";
// import Card from "../Components/Card";
// // import { Button } from "@/components/ui/button";
// // import {
// //   Tabs,
// //   TabsList,
// //   TabsTrigger,
// // } from "@/components/ui/tabs";
// import {
//   DropdownMenu,
//   DropdownMenuContent,
//   DropdownMenuItem,
//   DropdownMenuTrigger,
// } from "@/components/ui/dropdown-menu";
// import BlueButton from "../Components/BlueButton";
// import { BRAND } from "../css/brand";

// const chartData = [
//   { day: "1", rec: 1, chalte: 0 },
//   { day: "2", rec: 6, chalte: 1 },
//   { day: "3", rec: 0, chalte: 3 },
//   { day: "4", rec: 0, chalte: 0 },
//   { day: "5", rec: 2, chalte: 0 },
//   { day: "6", rec: 2, chalte: 1 },
//   { day: "7", rec: 0, chalte: 0 },
//   { day: "8", rec: 6, chalte: 2 },
//   { day: "9", rec: 0, chalte: 0 },
//   { day: "10", rec: 6, chalte: 3 },
//   { day: "11", rec: 0, chalte: 0 },
//   { day: "12", rec: 0, chalte: 2 },
//   { day: "13", rec: 4, chalte: 0 },
//   { day: "14", rec: 5, chalte: 6 },
//   { day: "15", rec: 0, chalte: 4 },
//   { day: "16", rec: 6, chalte: 5 },
// ];

// export default function UserViewPage() {
//   const { id } = useParams();
//   const navigate = useNavigate();
//   const [user, setUser] = useState(null);
//   const [loading, setLoading] = useState(true);

//   const [activeTab, setActiveTab] = useState("personal");

//   const tabs = [
//     { id: "personal", label: "Personal" },
//     { id: "keydetails", label: "Key Details" },
//     { id: "keyreports", label: "Key Reports" },
//     { id: "retailers", label: "Retailers" },
//     { id: "customers", label: "Customers" },
//   ];

//   useEffect(() => {
//     async function fetchUser() {
//       try {
//         const res = await axios.get(`https://spillas.com/api/v1/org_user/${id}`);
//         setUser(res.data?.data || res.data);
//       } catch (err) {
//         console.error("Failed to fetch user details", err);
//       } finally {
//         setLoading(false);
//       }
//     }
//     fetchUser();
//   }, [id]);

//   if (loading)
//     return (
//       <div className="flex h-screen items-center justify-center text-gray-500">
//         Loading user details...
//       </div>
//     );

//   if (!user)
//     return (
//       <div className="flex h-screen items-center justify-center text-red-500">
//         Failed to load user details.
//       </div>
//     );

//   return (
//     <div className="min-h-screen bg-[radial-gradient(60%_60%_at_20%_0%,#dbeafe_0,#fff_60%),radial-gradient(70%_60%_at_100%_0%,#e0e7ff_0,#fff_60%)]">
//       {/* Top bar */}
//       <div className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/70 backdrop-blur">
//         <div className="mx-auto flex h-14 max-w-[1200px] items-center justify-between px-4">
//           <div className="flex items-center gap-3">
//             <div className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-sm">
//               <Shield className="h-5 w-5" />
//             </div>
//             <div className="leading-tight">
//               <p className="text-xs text-slate-500">R.R. TECHNOLOGY</p>
//               <p className="text-sm font-semibold">Distributor Portal</p>
//             </div>
//           </div>
//           <div className="flex items-center gap-2">
//             <Button
//               variant="outline"
//               className="rounded-xl"
//               onClick={() => navigate(-1)}
//             >
//               ← Back
//             </Button>
//             <div className="grid h-8 w-8 place-items-center rounded-full bg-gradient-to-br from-indigo-500 to-blue-500 text-white">
//               {user.business_name?.[0] || "U"}
//             </div>
//           </div>
//         </div>
//       </div>

//       <div className="mx-auto max-w-[1200px] p-4">
//         {/* Header card */}
//         <motion.div
//           initial={{ opacity: 0, y: 12 }}
//           animate={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.35 }}
//         >
//           <Card className="overflow-hidden border-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-xl">
//             {/* <CardContent className="p-0"> */}
//               <div className="flex flex-col gap-6 p-6 md:flex-row md:items-center md:justify-between">
//                 <div className="flex items-center gap-4">
//                   <div className="grid h-16 w-16 place-items-center rounded-2xl bg-white/15 text-white ring-1 ring-white/20">
//                     <Shield className="h-8 w-8" />
//                   </div>
//                   <div>
//                     <div className="flex flex-wrap items-center gap-2">
//                       <h1 className="text-xl font-bold tracking-tight">
//                         {user.business_name || "—"}
//                       </h1>
//                       <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold">
//                         {user.role || "Distributor"}
//                       </span>
//                     </div>
//                     <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-blue-100">
//                       <a
//                         className="flex items-center gap-1 hover:underline"
//                         href={`mailto:${user.email}`}
//                       >
//                         <Mail className="h-3.5 w-3.5" /> {user.email || "—"}
//                       </a>
//                       <span className="flex items-center gap-1">
//                         <Phone className="h-3.5 w-3.5" /> {user.mobile || "—"}
//                       </span>
//                       <span className="flex items-center gap-1">
//                         <MapPin className="h-3.5 w-3.5" />{" "}
//                         {user.district}, {user.state} {user.pincode || ""}
//                       </span>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Insight pills */}
//                 <div className="grid grid-cols-2 gap-3 sm:grid-cols-2">
//                   <div className="rounded-2xl bg-white/10 px-5 py-3 text-center shadow-sm ring-1 ring-white/15">
//                     <p className="text-[10px] uppercase tracking-widest opacity-80">
//                       Recommended
//                     </p>
//                     <p className="mt-1 text-2xl font-bold leading-none">
//                       {user.rec_count || 0}
//                     </p>
//                   </div>
//                   <div className="rounded-2xl bg-white/10 px-5 py-3 text-center shadow-sm ring-1 ring-white/15">
//                     <p className="text-[10px] uppercase tracking-widest opacity-80">
//                       Chalte
//                     </p>
//                     <p className="mt-1 text-2xl font-bold leading-none">
//                       {user.chalte_count || 0}
//                     </p>
//                   </div>
//                 </div>
//               </div>

//                <div className="w-full bg-gradient-to-r from-blue-700/40 to-indigo-700/40 px-6 pb-4 pt-3">
//                 <div className="flex items-center justify-center">
//                   <div className="flex space-x-2 bg-white/10 p-1 rounded-xl backdrop-blur-md">
//                     {tabs.map((tab) => (
//                       <button
//                         key={tab.id}
//                         onClick={() => setActiveTab(tab.id)}
//                         className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 
//                           ${
//                             activeTab === tab.id
//                               ? "text-blue-700"
//                               : "text-white hover:text-blue-200"
//                           }`}
//                       >
//                         {activeTab === tab.id && (
//                           <motion.div
//                             layoutId="active-pill"
//                             className="absolute inset-0 bg-white rounded-lg shadow-md"
//                             transition={{ type: "spring", duration: 0.5 }}
//                           />
//                         )}
//                         <span className="relative z-10">{tab.label}</span>
//                       </button>
//                     ))}
//                   </div>
//                 </div>

//                 {/* ✅ Tab Content */}
//                 <div className="mt-6">
//                   {activeTab === "personal" && <p>Personal Info Section</p>}
//                   {activeTab === "keydetails" && <p>Key Details Section</p>}
//                   {activeTab === "keyreports" && <p>Key Reports Section</p>}
//                   {activeTab === "retailers" && <p>Retailers Section</p>}
//                   {activeTab === "customers" && <p>Customers Section</p>}
//                 </div>
//               </div>
//             {/* </CardContent> */}
//           </Card>
//         </motion.div>

//         {/* KPIs & Quick actions */}
//         <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
//           {[{label:"Active Retailers",value:"132"},{label:"Pending Tickets",value:"7"},{label:"New Signups",value:"24"},{label:"Churn Risk",value:"3"}].map((k,idx)=> (
//             <motion.div key={k.label} initial={{opacity:0,y:8}} animate={{opacity:1,y:0}} transition={{delay:0.05*idx}}>
//               <Card className="rounded-2xl border-slate-200/70 shadow-sm">
//                 <CardContent className="p-4">
//                   <p className="text-xs text-slate-500">{k.label}</p>
//                   <p className="mt-2 text-2xl font-semibold text-slate-800">{k.value}</p>
//                 </CardContent>
//               </Card>
//             </motion.div>
//           ))}
//         </div>

//         {/* Main content */}
//         <div className="mt-6 grid gap-6 lg:grid-cols-2">
//           {/* Profile card */}
//           <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
//             <Card className="rounded-2xl border-slate-200/70 shadow-md">
//               {/* <CardContent className="p-6"> */}
//                 <div className="mb-6 flex items-center gap-4">
//                   <div className="grid h-16 w-16 place-items-center rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow">
//                     <BadgeCheck className="h-8 w-8" />
//                   </div>
//                   <div>
//                     <div className="rounded-full bg-blue-50 px-3 py-0.5 text-xs font-semibold text-blue-700 shadow-sm">DIST34</div>
//                     <h3 className="mt-2 text-lg font-semibold">NAVNEET KUMAR</h3>
//                     <p className="text-xs text-slate-500">P.M. ENTERPRISES</p>
//                   </div>
//                 </div>

//                 <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
//                   <div className="rounded-xl border bg-white p-4">
//                     <p className="text-xs uppercase tracking-wide text-slate-500">Contact</p>
//                     <div className="mt-2 space-y-2">
//                       <a href="#" className="flex items-center gap-2 text-blue-600 hover:underline"><Mail className="h-4 w-4"/> navneet090189@gmail.com</a>
//                       <div className="flex items-center gap-2"><Phone className="h-4 w-4"/> 9027056827</div>
//                       <div className="flex items-center gap-2"><MapPin className="h-4 w-4"/> Mumbai, Maharashtra</div>
//                     </div>
//                   </div>

//                   <div className="rounded-xl border bg-white p-4">
//                     <p className="text-xs uppercase tracking-wide text-slate-500">Identifiers</p>
//                     <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
//                       <div>
//                         <p className="text-xs text-slate-500">District</p>
//                         <p className="font-medium">Mumbai</p>
//                       </div>
//                       <div>
//                         <p className="text-xs text-slate-500">State</p>
//                         <p className="font-medium">Maharashtra</p>
//                       </div>
//                       <div>
//                         <p className="text-xs text-slate-500">Pincode</p>
//                         <p className="font-medium">243001</p>
//                       </div>
//                       <div>
//                         <p className="text-xs text-slate-500">PAN</p>
//                         <p className="font-medium">123456</p>
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 <div className="mt-6 flex flex-wrap gap-3">
//                   <BlueButton brand={BRAND} variant="default" className="rounded-xl bg-blue-600 hover:bg-blue-700">Edit</BlueButton>
//                   <BlueButton brand={BRAND} variant="outline" className="rounded-xl">Export</BlueButton>
//                   <BlueButton brand={BRAND} variant="destructive" className="rounded-xl">Delete</BlueButton>
//                 </div>
//               {/* </CardContent> */}
//             </Card>
//           </motion.div>

//           {/* Chart card */}
//           <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
//             <Card className="rounded-2xl border-slate-200/70 shadow-md">
//               {/* <CardContent className="p-6"> */}
//                 <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
//                   <div>
//                     <h4 className="text-base font-semibold">Registered by Day</h4>
//                     <p className="text-xs text-slate-500">Customers</p>
//                   </div>
//                   <div className="flex items-center gap-2">
//                     <DropdownMenu>
//                       <DropdownMenuTrigger asChild>
//                         <Button variant="outline" className="rounded-xl">October</Button>
//                       </DropdownMenuTrigger>
//                       <DropdownMenuContent align="end" className="rounded-xl">
//                         {["August","September","October","November"].map(m => (
//                           <DropdownMenuItem key={m}>{m}</DropdownMenuItem>
//                         ))}
//                       </DropdownMenuContent>
//                     </DropdownMenu>
//                     <DropdownMenu>
//                       <DropdownMenuTrigger asChild>
//                         <Button variant="outline" className="rounded-xl">2025</Button>
//                       </DropdownMenuTrigger>
//                       <DropdownMenuContent align="end" className="rounded-xl">
//                         {[2023,2024,2025].map(y => (
//                           <DropdownMenuItem key={y}>{y}</DropdownMenuItem>
//                         ))}
//                       </DropdownMenuContent>
//                     </DropdownMenu>
//                   </div>
//                 </div>

//                 <div className="h-72 w-full">
//                   <ResponsiveContainer width="100%" height="100%">
//                     <BarChart data={chartData} barSize={18}>
//                       <CartesianGrid strokeDasharray="3 3" />
//                       <XAxis dataKey="day" tickMargin={8} />
//                       <YAxis allowDecimals={false} tickMargin={8} />
//                       <Tooltip cursor={{ fill: "rgba(59,130,246,0.08)" }} />
//                       <Bar dataKey="rec" name="Recommended" radius={[6,6,0,0]} fill="#3b82f6" />
//                       <Bar dataKey="chalte" name="Chalte" radius={[6,6,0,0]} fill="#818cf8" />
//                     </BarChart>
//                   </ResponsiveContainer>
//                 </div>

//                 <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
//                   <div className="flex items-center gap-2"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-blue-500"/> Recommended</div>
//                   <div className="flex items-center gap-2"><span className="inline-block h-2.5 w-2.5 rounded-sm bg-indigo-400"/> Chalte</div>
//                 </div>
//               {/* </CardContent> */}
//             </Card>
//           </motion.div>
//         </div>

//         <footer className="px-2 pb-8 pt-6 text-center text-xs text-slate-400">
//           Built with React + Tailwind + Recharts + Framer Motion. Layout inspired by RR Technology panel.
//         </footer>
//       </div>
//     </div>
//   );
// }